import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { NotificationTemplateDto } from "@/features/notification/api/dto";
import { NotificationTemplateRowActions } from "@/features/notification/components/templates/notification-template-row-actions";
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_LANGUAGES,
} from "@/features/shared/api/enums";

/**
 * Ligne de la table : `DataTableClar` exige un `id` requis ; le modèle n'a
 * pas d'id numérique côté API (le tuple `(code, channel, language)` est la
 * clé), on projette une clé composite stable pour React.
 */
export type NotificationTemplateRow = NotificationTemplateDto & { id: string };

/** Construit la clé composite à partir du tuple métier. */
export const buildTemplateRowId = (t: NotificationTemplateDto): string =>
  `${t.code}::${t.channel}::${t.language}`;

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

/**
 * Colonnes de la liste des modèles (`GET /NotificationTemplates` — mode
 * client de `DataTableClar` : pas de pagination serveur, filtres select
 * sur `channel`/`language`). Identifiants et références en `NoTranslate`
 * (règle notranslate).
 */
export const notificationTemplatesColumns: ColumnsClarFactory<NotificationTemplateRow> =
  () => [
    {
      id: "code",
      accessorKey: "code",
      header: t("notifications.templates.columns.code"),
      cell: ({ getValue }) => (
        <NoTranslate as="span" className="font-mono text-xs">
          {(getValue() as string) || "—"}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "channel",
      accessorKey: "channel",
      header: t("notifications.templates.columns.channel"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">
          <Badge variant="secondary">{getValue() as string}</Badge>
        </NoTranslate>
      ),
      meta: { type: "select", options: selectOptions(NOTIFICATION_CHANNELS) },
    },
    {
      id: "language",
      accessorKey: "language",
      header: t("notifications.templates.columns.language"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">
          <Badge variant="outline">{getValue() as string}</Badge>
        </NoTranslate>
      ),
      meta: { type: "select", options: selectOptions(NOTIFICATION_LANGUAGES) },
    },
    {
      id: "subject",
      accessorKey: "subject",
      header: t("notifications.templates.columns.subject"),
      cell: ({ getValue }) => {
        const value = getValue() as string | null | undefined;
        return value ? (
          <span className="line-clamp-1">{value}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
      meta: { type: "text" },
    },
    {
      id: "isActive",
      accessorKey: "isActive",
      header: t("notifications.templates.columns.isActive"),
      cell: ({ getValue }) => {
        const active = getValue() as boolean | undefined;
        return active ? (
          <Badge
            variant="outline"
            className="border-success/40 bg-success-muted text-success"
          >
            {t("notifications.templates.status.active")}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-border bg-muted text-muted-foreground"
          >
            {t("notifications.templates.status.inactive")}
          </Badge>
        );
      },
      meta: { type: "text" },
    },
    {
      id: "updatedAt",
      accessorKey: "updatedAt",
      header: t("notifications.templates.columns.updatedAt"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">
          {formatDateWithFallback(getValue() as string | null, "—")}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "actions",
      header: t("common.actions"),
      enableHiding: false,
      cell: ({ row }) => (
        <NotificationTemplateRowActions template={row.original} />
      ),
      meta: { type: "denied" },
    },
  ];
